import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  LanguageSelector,
  Separator,
  useSelectedHost,
} from "@tellescope/ui";
import { isDevelopment } from "@tellescope/utils";
import { HostSelector } from "../../components/HostSelector";
import { DevBanner } from "../../components/DevBanner";
import { SSOLoginButton } from "./SSOLoginButton";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { useTranslation } from "react-i18next";
import { useAuth } from "@tellescope/tanstack-query";

export const LoginPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedHost, setSelectedHost] = useSelectedHost();
  const [bannerVisible, setBannerVisible] = useState(true);
  const [searchParams] = useSearchParams();
  const configurationId = searchParams.get("configurationId") ?? undefined;
  const oktaId = searchParams.get("oktaId") ?? undefined;

  const { login, loginError: error, isAuthenticated, requiresMFA } = useAuth();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to MFA page if MFA is required
  if (requiresMFA) {
    return <Navigate to="/mfa" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;

    setIsLoading(true);

    try {
      await login({ email, password });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isDevelopment() && bannerVisible && <DevBanner onClose={() => setBannerVisible(false)} />}
      <div className="min-h-screen bg-background flex items-center justify-center p-8 relative">
        {/* Language selector in top-right corner */}
        <div className="absolute top-4 right-4">
          <LanguageSelector size="sm" />
        </div>

        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">{t("Welcome Back")}</CardTitle>
            <CardDescription>{t("Sign in to your Tellescope account")}</CardDescription>
          </CardHeader>
          <CardContent>
            {isDevelopment() && (
              <div className="mb-6 p-4 bg-blue-50 rounded-md border border-blue-200">
                <HostSelector onHostChange={setSelectedHost} />
                {selectedHost && (
                  <p className="mt-2 text-xs text-blue-600">
                    {t("Connected to")}: {selectedHost.name} ({selectedHost.url})
                  </p>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t("Email")}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="" // Will be handled by translation system later
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t("Password")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="" // Will be handled by translation system later
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  {typeof error === "string" ? error : error.message}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || !email || !password}>
                {isLoading ? t("Signing in...") : t("Sign In")}
              </Button>
            </form>

            <div className="relative my-4">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                {t("or")}
              </span>
            </div>

            <div className="space-y-2">
              <GoogleLoginButton />
              <SSOLoginButton provider="microsoft-oidc" configurationId={configurationId} />
              {oktaId && <SSOLoginButton provider="okta-oidc" configurationId={oktaId} />}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
